import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import DbDatePicker from '../DbDatePicker';
import InviteePicker from './InviteePicker';
import TimeCombobox, { timeKeyToMinutes } from './TimeCombobox';
import EditorSelect from './EditorSelect';
import RecurrenceEditor from './RecurrenceEditor';
import { fetchSentInvitations, useCalendarInvitesStore } from '../../services/calendarInvitesService';
import { useAuthStore } from '../../store/authStore';
import { useTeamsStore } from '../../store/teamsStore';
import { calendarTeamId } from '../../extensions/bundled/calendar/calendarStore';
import { Trash2, Plus } from 'lucide-react';
import { toDateKey, fromDateKey } from '../../utils/calendarDates';
import { usePreferencesStore } from '../../store/preferencesStore';

const EMPTY_SENT_ROWS = [];

// KEY table, resolved at RENDER: a module-scope `t()` would freeze English at
// import time and never follow a language switch.
const REMINDER_OPTION_KEYS = [
  { minutes: 0, labelKey: 'calendar.reminder.atStart' },
  { minutes: 5, labelKey: 'calendar.reminder.min5' },
  { minutes: 10, labelKey: 'calendar.reminder.min10' },
  { minutes: 15, labelKey: 'calendar.reminder.min15' },
  { minutes: 30, labelKey: 'calendar.reminder.min30' },
  { minutes: 60, labelKey: 'calendar.reminder.hour1' },
  { minutes: 1440, labelKey: 'calendar.reminder.day1' },
];

function pad(n) {
  return String(n).padStart(2, '0');
}

function localTimeKey(date) {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
}

function composeLocal(dateKey, timeKey) {
  const base = fromDateKey(dateKey) || new Date();
  const m = /^(\d{1,2}):(\d{2})/.exec(String(timeKey || '00:00'));
  base.setHours(m ? Number(m[1]) : 0, m ? Number(m[2]) : 0, 0, 0);
  return base;
}

function initialFields(draft) {
  const now = new Date();
  if (draft?.all_day) {
    return {
      startDate: draft.start_date || toDateKey(now),
      endDate: draft.end_date || draft.start_date || toDateKey(now),
      startTime: '09:00:00',
      endTime: '10:00:00',
    };
  }
  const start = draft?.start ? new Date(draft.start) : now;
  const end = draft?.end ? new Date(draft.end) : new Date(start.getTime() + 30 * 60000);
  return {
    startDate: toDateKey(start),
    endDate: toDateKey(end),
    startTime: localTimeKey(start),
    endTime: localTimeKey(end),
  };
}

/**
 * Full event editor (PLAN §3.5): title, all-day toggle, date + time via the
 * shared DbDatePicker popovers, location, description, reminders. Events
 * color BY CALENDAR (owner, 30.08) — no per-event color control.
 * Deleting goes through the ConfirmModal CalendarView hosts — never
 * window.confirm (it THROWS in the packaged app). Times are edited in local
 * wall-clock terms and stored as ISO UTC on save (§3.2).
 */
export default function EventEditorModal({ draft, onSave, onDelete, onClose }) {
  const { t } = useTranslation();
  const isExisting = Boolean(draft?.uid);
  const timeFormat = usePreferencesStore((s) => s.timeFormat);
  const init = useMemo(() => initialFields(draft), [draft]);

  const [title, setTitle] = useState(draft?.title || '');
  const [allDay, setAllDay] = useState(draft?.all_day === true);
  const [startDate, setStartDate] = useState(init.startDate);
  const [endDate, setEndDate] = useState(init.endDate);
  const [startTime, setStartTime] = useState(init.startTime);
  const [endTime, setEndTime] = useState(init.endTime);
  const [location, setLocation] = useState(draft?.location || '');
  const [description, setDescription] = useState(draft?.description || '');
  const [reminders, setReminders] = useState(
    Array.isArray(draft?.reminders) && draft.reminders.length > 0
      ? draft.reminders.map((r) => Number(r.minutes_before))
      : []
  );
  const [picker, setPicker] = useState(null); // { field, kind, anchorRect }
  const [invitees, setInvitees] = useState([]); // new picks (users to invite on Save)
  const [titleError, setTitleError] = useState(null); // client-side guard (polish round)
  const [calendar, setCalendar] = useState(draft?.calendar || 'personal'); // Phase 3
  // Phase 4: the recurrence rule (masters + occurrences of them). Overrides
  // never show the row — editing the rule from an exception is not offered.
  const [recurrence, setRecurrence] = useState(draft?.recurrence || null);
  const isOverride = Boolean(draft?.override_of);
  const teams = useTeamsStore((s) => s.teams);
  // Calendar picker offers Personal + every team where the user can WRITE
  // (editor+ — the server's team-sync push rule; viewers never see a team
  // option, mirroring enforcement instead of faking it).
  // `team`, not `t` — `t` is the translation function in this scope now.
  const writableTeams = (teams || []).filter((team) => {
    if (team?.id == null) return false;
    const role = team.role || (team.is_owner ? 'owner' : null);
    return role != null && role !== 'viewer';
  });
  const isTeamCalendar = calendarTeamId(calendar) != null;
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Selector must return a STABLE value when the key is absent — a fresh []
  // per call makes useSyncExternalStore re-render forever (Object.is on a
  // new reference every snapshot).
  const sentRows =
    useCalendarInvitesStore((s) => (isExisting ? s.sentByAppointment[draft.uid] : null)) ||
    EMPTY_SENT_ROWS;
  // A materialized copy of someone ELSE'S appointment — the invitee edits
  // nothing invitation-wise here; no picker for it.
  const isInviteeCopy = Boolean(draft?.origin);
  // Card 25e3e5a2: a move re-creates the event under a new uid. An exception
  // would leave its base occurrence behind; sent invitations stay on the old
  // uid the server knows. So neither may switch calendars.
  const calendarLock = !isExisting ? null
    : isOverride ? 'calendarLockedOverride'
      : sentRows.length > 0 ? 'calendarLockedInvites' : null;
  const showInvitees = isAuthenticated && !isInviteeCopy;

  // Organizer opening an existing event: load its sent rows once so the
  // picker shows live invitee statuses and Save knows to PATCH.
  useEffect(() => {
    if (!isExisting || !showInvitees) return;
    fetchSentInvitations(draft.uid).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.uid]);

  const openPicker = (field, kind) => (e) => {
    setPicker({ field, kind, anchorRect: e.currentTarget.getBoundingClientRect() });
  };

  const pickerValue =
    picker?.field === 'startDate'
      ? startDate
      : picker?.field === 'endDate'
        ? endDate
        : picker?.field === 'startTime'
          ? startTime
          : endTime;

  const handlePick = (value) => {
    if (!picker) return;
    if (picker.field === 'startDate') {
      setStartDate(value);
      // Keep the end on/after the start (Outlook keeps the duration; keeping
      // the same-day default is enough for v1 and never produces end<start).
      if (value > endDate) setEndDate(value);
    } else if (picker.field === 'endDate') {
      setEndDate(value < startDate ? startDate : value);
    } else if (picker.field === 'startTime') {
      setStartTime(value);
    } else {
      setEndTime(value);
    }
    setPicker(null);
  };

  const handleSave = () => {
    // Client-side guard (polish round): the backend 422s an untitled snapshot,
    // but a raw server string is not an editor experience — block pre-POST
    // with a human message when the event is being SHARED. Untitled private
    // events stay allowed (unchanged Phase 1 behaviour).
    if (!isTeamCalendar && (invitees.length > 0 || sentRows.length > 0) && !title.trim()) {
      setTitleError(t('calendar.editor.titleRequired'));
      return;
    }
    const fields = {
      ...draft,
      calendar,
      // Clamp in-progress text values ('' while retyping) at save — the
      // fields themselves clamp on blur, but Save can fire mid-edit.
      recurrence:
        isOverride || !recurrence
          ? null
          : {
              ...recurrence,
              interval: Math.max(1, Math.min(99, Number(recurrence.interval) || 1)),
              ...(recurrence.count !== undefined
                ? { count: Math.max(1, Math.min(999, Number(recurrence.count) || 1)) }
                : {}),
            },
      title: title.trim(),
      location: location.trim(),
      description,
      all_day: allDay,
      reminders: reminders.map((m) => ({ minutes_before: m })),
    };
    if (allDay) {
      fields.start_date = startDate;
      fields.end_date = endDate < startDate ? startDate : endDate;
      fields.start = null;
      fields.end = null;
    } else {
      let start = composeLocal(startDate, startTime);
      let end = composeLocal(endDate, endTime);
      if (end <= start) end = new Date(start.getTime() + 30 * 60000);
      fields.start = start.toISOString();
      fields.end = end.toISOString();
      fields.start_date = null;
      fields.end_date = null;
    }
    onSave?.(fields, {
      invitees: isTeamCalendar || fields.recurrence ? [] : invitees,
      hasInvitations: !isTeamCalendar && !fields.recurrence && sentRows.length > 0,
    });
  };

  // Time entry is a COMBOBOX (owner, 30.08): 15-min dropdown in the user's
  // 12h/24h format + free typing, garbage reverts. The END field shows
  // Google-style duration hints relative to the start.
  const timeCombobox = (field, value) => (
    <TimeCombobox
      value={value}
      timeFormat={timeFormat}
      ariaLabel={t(field === 'startTime' ? 'calendar.editor.startTime' : 'calendar.editor.endTime')}
      startMinutes={field === 'endTime' ? timeKeyToMinutes(startTime) : null}
      minMinutes={
        // Same-day end: list only times after the start; a later end date
        // gets the full list (owner refinement).
        field === 'endTime' && endDate === startDate ? timeKeyToMinutes(startTime) : null
      }
      onCommit={(timeKey) => (field === 'startTime' ? setStartTime(timeKey) : setEndTime(timeKey))}
    />
  );
  // Date TRIGGER wears the same modal-input frame as every other editor
  // field (owner: one border language); the popover stays the shared
  // DbDatePicker — wrapped, not forked.
  const dateButton = (field, value) => (
    <button
      type="button"
      className="modal-input"
      style={{ width: 130, textAlign: 'left', cursor: 'pointer' }}
      aria-label={t(field === 'startDate' ? 'calendar.editor.startDate' : 'calendar.editor.endDate')}
      onClick={openPicker(field, 'date')}
    >
      {value}
    </button>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t(isExisting ? 'calendar.editor.titleEdit' : 'calendar.newEvent')}
      size="md"
    >
      <div className="cal-editor-grid">
        <input
          className="modal-input"
          style={{ fontWeight: 600, fontSize: 14 }}
          placeholder={t('calendar.editor.addTitle')}
          value={title}
          aria-label={t('calendar.editor.title')}
          aria-invalid={titleError ? true : undefined}
          autoFocus
          onChange={(e) => {
            setTitle(e.target.value);
            if (titleError) setTitleError(null);
          }}
        />
        {titleError && (
          <span className="cal-editor-hint" role="alert" style={{ color: '#e5484d' }}>
            {titleError}
          </span>
        )}

        <div className="cal-editor-row">
          <span className="cal-editor-label">{t('calendar.editor.starts')}</span>
          {dateButton('startDate', startDate)}
          {!allDay && timeCombobox('startTime', startTime)}
        </div>
        {draft?.is_occurrence && (
          // P4-N3 subcopy: a day change on a "This event" edit doesn't travel
          // the way people expect — say so instead of staying silent.
          <span className="cal-editor-hint">{t('calendar.editor.occurrenceMoveHint')}</span>
        )}
        <div className="cal-editor-row">
          <span className="cal-editor-label">{t('calendar.editor.ends')}</span>
          {dateButton('endDate', endDate)}
          {!allDay && timeCombobox('endTime', endTime)}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
            <input
              type="checkbox"
              className="checkbox-brand"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
            />
            {t('calendar.allDay')}
          </label>
        </div>

        {!isOverride && (
          <div className="cal-editor-row" style={{ alignItems: 'flex-start' }}>
            <span className="cal-editor-label" style={{ paddingTop: 6 }}>
              {t('calendar.recurrence.repeats')}
            </span>
            <RecurrenceEditor
              recurrence={recurrence}
              onChange={setRecurrence}
              seriesStartDay={fromDateKey(startDate) || new Date()}
            />
          </div>
        )}

        {(writableTeams.length > 0 || isTeamCalendar) && (
          <div className="cal-editor-row">
            <span className="cal-editor-label">{t('calendar.editor.calendar')}</span>
            <EditorSelect
              ariaLabel={t('calendar.editor.calendar')}
              value={calendar}
              onChange={(v) => setCalendar(v)}
              options={[
                { value: 'personal', label: t('calendar.filter.personal') },
                ...writableTeams.map((team) => ({
                  value: `team:${team.id}`,
                  label: team.name || t('calendar.editor.teamN', { id: team.id }),
                })),
              ].filter((o) => !calendarLock || o.value === calendar)}
            />
            {calendarLock && <span className="cal-editor-hint">{t(`calendar.editor.${calendarLock}`)}</span>}
          </div>
        )}

        <div className="cal-editor-row">
          <span className="cal-editor-label">{t('calendar.editor.location')}</span>
          <input
            className="modal-input"
            style={{ flex: 1, width: 'auto' }}
            placeholder={t('calendar.editor.addLocation')}
            value={location}
            aria-label={t('calendar.editor.location')}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>

        <div className="cal-editor-row" style={{ alignItems: 'flex-start' }}>
          <span className="cal-editor-label" style={{ paddingTop: 6 }}>
            {t('calendar.editor.reminders')}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
            {reminders.map((minutes, i) => (
              <div key={i} className="cal-reminder-row">
                <EditorSelect
                  value={minutes}
                  ariaLabel={t('calendar.editor.reminderN', { n: i + 1 })}
                  options={REMINDER_OPTION_KEYS.map((o) => ({
                    value: o.minutes,
                    label: t(o.labelKey),
                  }))}
                  onChange={(v) =>
                    setReminders((rs) => rs.map((m, j) => (j === i ? Number(v) : m)))
                  }
                />
                <button
                  type="button"
                  className="cal-btn cal-btn--icon"
                  aria-label={t('calendar.editor.removeReminder')}
                  onClick={() => setReminders((rs) => rs.filter((_, j) => j !== i))}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            {reminders.length < 5 && (
              <button
                type="button"
                className="cal-btn"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => setReminders((rs) => [...rs, 10])}
              >
                <Plus size={13} />
                {t('calendar.editor.addReminder')}
              </button>
            )}
            <span className="cal-editor-hint">{t('calendar.editor.remindersHint')}</span>
          </div>
        </div>

        {showInvitees && !isTeamCalendar && !(recurrence || isOverride) && (
          <div className="cal-editor-row" style={{ alignItems: 'flex-start' }}>
            <span className="cal-editor-label" style={{ paddingTop: 6 }}>
              {t('calendar.editor.invitees')}
            </span>
            <InviteePicker invitees={invitees} onChange={setInvitees} sentRows={sentRows} />
          </div>
        )}
        {showInvitees && !isTeamCalendar && (recurrence || isOverride) && (
          <div className="cal-editor-row">
            <span className="cal-editor-label">{t('calendar.editor.invitees')}</span>
            {/* Owner-approved v1 line: per-occurrence invitation state is a
                different data model — lands with "this and following". */}
            <span className="cal-editor-hint">{t('calendar.invite.recurringNotShareable')}</span>
          </div>
        )}
        {showInvitees && isTeamCalendar && (
          <div className="cal-editor-row">
            <span className="cal-editor-label">{t('calendar.editor.invitees')}</span>
            {/* v1 product line: team events carry NO invitations — every
                member already sees them through team sync. */}
            <span className="cal-editor-hint">{t('calendar.invite.teamNoInvites')}</span>
          </div>
        )}

        <div className="cal-editor-row" style={{ alignItems: 'flex-start' }}>
          <span className="cal-editor-label" style={{ paddingTop: 6 }}>
            {t('calendar.editor.notes')}
          </span>
          <textarea
            className="modal-input"
            style={{ flex: 1, width: 'auto', resize: 'vertical', minHeight: 56 }}
            placeholder={t('calendar.editor.addDescription')}
            value={description}
            aria-label={t('calendar.editor.description')}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="cal-editor-footer">
          {isExisting && onDelete && (
            <button type="button" className="cal-btn" onClick={() => onDelete(draft)}>
              <Trash2 size={13} />
              {t('common.delete')}
            </button>
          )}
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
            <button type="button" className="cal-btn" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="button" className="cal-btn cal-btn--primary" onClick={handleSave}>
              {t('common.save')}
            </button>
          </span>
        </div>
      </div>

      {picker && (
        <DbDatePicker
          kind={picker.kind}
          value={pickerValue}
          anchorRect={picker.anchorRect}
          onPick={handlePick}
          onClear={() => setPicker(null)}
          onClose={() => setPicker(null)}
        />
      )}
    </Modal>
  );
}
